import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What are the four phases of a dividend investor\'s life?',
    a: 'Phase 1 — Accumulator (20s-40s): all DRIP, max contributions, broad ETFs. Phase 2 — Growth-Income (40s-55): start mixing in individual dividend growers, still mostly DRIP. Phase 3 — Bridge (55-65): begin taking some dividends as cash, build a "rising income" sleeve. Phase 4 — Retirement-Spend (65+): full distribution mode, every dividend funds living expenses.',
  },
  {
    q: 'Why does the phase matter for ticker selection?',
    a: 'Different phases reward different yield/growth trade-offs. Accumulators benefit from low-yield high-growth names (SCHD, MSFT, V) because dividend growth compounds against a long runway. Retirees need higher current yield with stability (O, JNJ, KO, utilities). The same investor will rotate across these as they age.',
  },
  {
    q: 'When should I switch from DRIP to cash dividends?',
    a: 'Two triggers: (1) you start needing the income to fund expenses, or (2) you reach the Bridge phase and want flexibility to redeploy. Most investors switch gradually — turning off DRIP on individual holdings one at a time as they accumulate enough shares.',
  },
  {
    q: 'Does the phase change my account-type strategy?',
    a: 'Yes. Accumulators max Roth contributions because tax-free growth has decades to compound. Bridge-phase investors prioritise tax-loss harvesting in taxable accounts. Retirees often start Roth conversions to manage future RMDs. The instrument is the same; the strategy shifts.',
  },
  {
    q: 'What\'s the biggest mistake in each phase?',
    a: 'Accumulator: chasing high yield instead of growth. Growth-Income: failing to diversify beyond US large caps. Bridge: not adjusting fast enough as retirement nears. Retirement-Spend: assuming dividends will always be paid (they cut in 2008-09 and 2020 — have a cash buffer).',
  },
];

export default function LearnFourPhases() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="The 4 Phases of a Dividend Investor's Life — From First Buy to Living on Income | DiviDrip"
      description="A practical roadmap for how priorities, ticker mix, and account strategy shift across the four phases of a dividend investing journey: Accumulator → Growth-Income → Bridge → Retirement-Spend."
      canonical="https://twylightcrow.com/learn/four-phases"
      guidePath="/learn/four-phases"
      sectionLabel="Strategy & Psychology"
      sectionAnchor="strategy"
      kicker="Learn · Lifecycle"
      h1="The 4 Phases of a Dividend Investor's Life"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/drip-vs-cash-dividends', label: 'DRIP vs cash dividends — which to choose?' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/why-drip-roth', label: 'Why I DRIP in my Roth but not in my taxable' },
        { to: '/learn/build-5k-monthly-income', label: 'Build a $5K monthly dividend income portfolio' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
      ]}
    >
      <p>
        Most dividend-investing advice treats every investor identically. A 28-year-old
        with a 40-year runway and a 67-year-old taking distributions are following the
        same checklist — and that's wrong. The right ticker mix, account choice, and
        DRIP setting depend entirely on which phase of the journey you're in.
      </p>
      <p>
        Here's a map of the four phases and what changes between them.
      </p>

      <h2>Phase 1 — Accumulator (20s to mid-40s)</h2>
      <p>
        You have decades of compounding ahead. Your single biggest weapon is time, and
        the right strategy is to maximise the number of shares you accumulate.
      </p>
      <ul>
        <li><strong>Priority:</strong> contribute consistently. The dollar amount per buy matters more than the ticker choice.</li>
        <li><strong>Account mix:</strong> max Roth IRA + 401(k) match first. Taxable comes last.</li>
        <li><strong>DRIP setting:</strong> on for everything. No exceptions.</li>
        <li><strong>Ticker mix:</strong> 70% broad dividend-growth ETFs (SCHD, DGRO, VYM), 20% quality Dividend Kings (KO, JNJ, PG, MSFT), 10% experimentation.</li>
        <li><strong>What to avoid:</strong> chasing 9%+ yields. You don't need the income; you need the growth.</li>
      </ul>

      <h2>Phase 2 — Growth-Income (mid-40s to mid-50s)</h2>
      <p>
        Your portfolio has real size now. Dividend income is starting to be visible
        (think $5K-$20K/year). You're thinking 10-15 years ahead.
      </p>
      <ul>
        <li><strong>Priority:</strong> diversify across sectors and add individual conviction names.</li>
        <li><strong>Account mix:</strong> still maxing tax-advantaged, but taxable starts mattering for early-retirement scenarios.</li>
        <li><strong>DRIP setting:</strong> on for ETFs; consider turning off on individual holdings as positions get large.</li>
        <li><strong>Ticker mix:</strong> 50% ETFs, 35% individual Kings + Aristocrats, 10% high-conviction growth (MSFT, V, COST), 5% higher-yield experiments (one BDC or REIT).</li>
        <li><strong>What to avoid:</strong> sector concentration. A portfolio that's 60% tech because tech has run is one bear market from a permanent capital impairment.</li>
      </ul>

      <h2>Phase 3 — Bridge (mid-50s to mid-60s)</h2>
      <p>
        Retirement is in sight. You're thinking about Social Security timing, healthcare,
        and most importantly: how to convert the portfolio you built into a paycheck.
      </p>
      <ul>
        <li><strong>Priority:</strong> build a "rising income" sleeve and start mentally moving from accumulation to distribution.</li>
        <li><strong>Account mix:</strong> consider Roth conversions to manage future RMDs.</li>
        <li><strong>DRIP setting:</strong> off on holdings you'll soon distribute from; still on for long-term growers.</li>
        <li><strong>Ticker mix:</strong> 40% ETFs, 30% Kings/Aristocrats, 15% utilities + REITs for income stability, 10% higher-yield (BDCs, CEFs), 5% experimentation.</li>
        <li><strong>What to avoid:</strong> selling growth positions too early to lock in a "safe" yield. You may have 30+ more years; you still need growth.</li>
      </ul>

      <h2>Phase 4 — Retirement-Spend (65+)</h2>
      <p>
        You're living on it now. Every dividend either funds expenses or gets selectively
        reinvested.
      </p>
      <ul>
        <li><strong>Priority:</strong> income stability and sequence-of-returns risk management. Keep a 1-2 year cash buffer so you don't have to sell during a downturn.</li>
        <li><strong>Account mix:</strong> spend from taxable first to preserve Roth growth, but balance against RMDs.</li>
        <li><strong>DRIP setting:</strong> off on most holdings. You're taking the cash.</li>
        <li><strong>Ticker mix:</strong> 30% ETFs (SCHD, VYM), 25% Kings/Aristocrats, 25% income (REITs, utilities, BDCs), 15% covered-call ETFs (JEPI, JEPQ) for current cash flow, 5% cash buffer.</li>
        <li><strong>What to avoid:</strong> assuming dividends will never be cut. 2008-09 and 2020 both saw broad dividend cuts. Diversify across sectors AND across dividend sustainability tiers.</li>
      </ul>

      <h2>The phase-transition mistake</h2>
      <p>
        The most common mistake isn't picking the wrong tickers — it's failing to
        recognise when you've crossed a phase boundary. Most investors stay in
        Accumulator mode emotionally long after their portfolio has crossed into
        Growth-Income. They keep DRIP'ing into the same broad ETFs when they should
        be diversifying. They keep adding to large positions when they should be
        starting smaller positions in complementary holdings.
      </p>
      <p>
        Pick a self-review cadence — once a year on your birthday is the simplest — and
        ask: "What phase am I really in?" If your portfolio has doubled since you last
        thought about this, the answer is probably "later than you think".
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Use DiviDrip to track your phase</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> and look at the{' '}
        <strong>Portfolio Summary</strong> section at the top of the My Portfolio
        page. The <strong>Annual Dividends</strong> tile carries a "$X/mo"
        subtitle — that's your current monthly dividend income. Compare it to
        your monthly expenses. The ratio tells you where you really are. Under
        5% = Accumulator. 5-25% = Growth-Income. 25-80% = Bridge. 80%+ =
        Retirement-Spend territory. The number is honest in a way that birthdays
        aren't.
      </p>
    </LearnPage>
  );
}
